import React, { useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Badge from 'react-bootstrap/Badge';
import Select from 'react-select';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { useTax1099Api } from '../../resources/hooks/api/useTax1099ApiHook';
import { useAllVendors } from '../../resources/useAllVendors';
import { URLS } from '../../resources/constants';
import { getBranch } from '../../store/user/selectors';
import VendorFormModal, { EMPTY_VENDOR, VendorFormValues } from '../Tax1099Vendors/VendorFormModal';

/**
 * Assign a vendor to the bank payments that were read off a cheque.
 *
 * Extraction proposes the payee; nothing is written to a 1099 until someone
 * confirms here. Confirmation covers exactly the transaction IDs this screen
 * displayed -- if the server has since seen others, it rejects the submission
 * as stale rather than quietly including them.
 */

interface PayeeTransaction {
  transaction_id: number;
  amount: number;
  posting_date?: string | null;
  description?: string | null;
  extracted_check_data?: any;
}

interface PayeeCluster {
  payee_key: string;
  vendor_name: string;
  transaction_count: number;
  total_amount: number;
  earliest_date?: string | null;
  latest_date?: string | null;
  suggested_vendors: Array<{ id: number; display_name: string; vendor_code?: string }>;
  transactions: PayeeTransaction[];
}

const ADDRESS_FIELDS = [
  'payee_address_line1', 'payee_address_line2', 'payee_city', 'payee_state', 'payee_postal_code',
] as const;

const addressOf = (cheque: any) => {
  if (!cheque) return null;
  const parts = ADDRESS_FIELDS.map((f) => String(cheque[f] ?? '').trim());
  return parts.some(Boolean) ? parts : null;
};

/**
 * The addresses a cluster's cheques carry, newest first, de-duplicated.
 *
 * A payee can be paid by several cheques, and they need not agree. Taking
 * whichever happened to be first in the list would pre-fill an arbitrary one
 * and hide the disagreement, so the newest is proposed (transaction id breaks a
 * date tie) and any others are offered for the reviewer to pick instead.
 * Partial addresses are never merged: each is whole or not used.
 */
const chequeAddresses = (cluster: PayeeCluster) => {
  const withAddress = cluster.transactions
    .filter((t) => addressOf(t.extracted_check_data))
    .sort((a, b) => {
      const byDate = String(b.posting_date || '').localeCompare(String(a.posting_date || ''));
      return byDate !== 0 ? byDate : b.transaction_id - a.transaction_id;
    });

  const seen = new Set<string>();
  return withAddress.filter((t) => {
    const key = (addressOf(t.extracted_check_data) || []).join('|');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const money = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

const Tax1099PayeeReview: React.FC = () => {
  const { t } = useTranslation('Tax1099PayeeReview');
  const branchData: any = useSelector(getBranch);
  const branchId = Number(branchData?.id);

  const {
    fetchUnmappedPayees, resolvePayee, createVendor, fetchNextVendorCode,
    openStatementEvidence,
  } = useTax1099Api();

  const [loading, setLoading] = useState(false);
  const [clusters, setClusters] = useState<PayeeCluster[]>([]);
  const { vendors: allVendors } = useAllVendors({ branchId });
  const [vendors, setVendors] = useState<Array<{ value: number; label: string }>>([]);
  const [chosen, setChosen] = useState<Record<string, number | null>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [creatingFor, setCreatingFor] = useState<PayeeCluster | null>(null);
  const [formInitial, setFormInitial] = useState<VendorFormValues>(EMPTY_VENDOR);
  const [creating, setCreating] = useState(false);
  // Set once a payee is resolved here, so the way on to deposit classification
  // is offered only to someone who came to finish a month close, not on every visit.
  const [resolvedAny, setResolvedAny] = useState(false);

  const load = useCallback(() => {
    if (!branchId) return;
    setLoading(true);
    fetchUnmappedPayees({ branch_id: branchId }, (_m: string, resp: any) => {
      setClusters(resp?.data?.data || []);
      setLoading(false);
    }, (message: string) => {
      setLoading(false);
      toast.error(message || t('errors.load_failed'));
    });
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  // Local state is kept so a freshly created vendor can be prepended and stay
  // selectable; it is seeded from the shared list rather than from a single
  // 200-row page, which used to hide every vendor past the 200th.
  useEffect(() => {
    setVendors(
      allVendors.map((v: any) => ({
        value: v.id,
        label: v.vendor_code ? `${v.display_name} (${v.vendor_code})` : v.display_name,
      })),
    );
  }, [allVendors]);

  /**
   * Open the bank statement the cheque was read from, at its page.
   *
   * The tab is opened synchronously on the click so the popup blocker lets it
   * through, then pointed at the blob once gl-api answers.
   */
  const openEvidence = (evidence: any) => {
    const tab = window.open('', '_blank');
    openStatementEvidence(
      { file_id: evidence.source_file_id, branch_id: branchId },
      (_m: string, blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const page = evidence.source_page ? `#page=${evidence.source_page}` : '';
        if (tab) { tab.location.href = url + page; } else { window.open(url + page, '_blank'); }
      },
      (message: string) => {
        tab && tab.close();
        toast.error(message || t('errors.source_unavailable'));
      },
    );
  };

  const assign = (cluster: PayeeCluster, vendorId: number) => {
    setSaving(cluster.payee_key);
    resolvePayee({
      branch_id: branchId,
      payee_key: cluster.payee_key,
      target_vendor_id: vendorId,
      // Exactly the rows shown, so confirmation cannot cover more than was read.
      transaction_ids: cluster.transactions.map((t) => t.transaction_id),
    }, (_m: string, resp: any) => {
      const data = resp?.data?.data || {};
      setSaving(null);
      // Both counts, because creating a vendor triggers a sync that can assign
      // an exact payee match before this confirmation arrives. Reporting only
      // the rows this request wrote would say "0 transaction(s)" for a cluster
      // it had in fact just resolved.
      const resolved = Number(data.updated_transactions || 0)
        + Number(data.already_assigned_transactions || 0);
      toast.success(t('toasts.assigned', {
        from: cluster.vendor_name,
        to: data.target_vendor_name,
        transactions: t('counts.transactions', { count: resolved }),
        payments: t('counts.payments', { count: Number(data.inserted_payments || 0) }),
      }));
      setResolvedAny(true);
      load();
    }, (message: string, resp: any) => {
      setSaving(null);
      const failure = resp?.data?.message || message || '';
      if (`${failure}`.includes('stale')) {
        toast.error(t('errors.stale'));
        load();
        return;
      }
      toast.error(failure || t('errors.assign_failed'));
    });
  };

  const confirm = (cluster: PayeeCluster) => {
    const vendorId = chosen[cluster.payee_key];
    if (!vendorId) {
      toast.error(t('errors.choose_vendor'));
      return;
    }
    assign(cluster, vendorId);
  };

  /**
   * Open the vendor form pre-filled from the cheque. The payee name and any
   * address the extractor was willing to stand behind come across; everything
   * else is left for the person to fill in, because a 1099 vendor carries more
   * than a cheque prints.
   */
  const startCreateFromCheque = (cluster: PayeeCluster, fromTransactionId?: number) => {
    const named = cluster.transactions.find((t) => t.extracted_check_data)?.extracted_check_data || {};
    const candidates = chequeAddresses(cluster);
    const chosenSource = fromTransactionId
      ? candidates.find((t) => t.transaction_id === fromTransactionId)
      : candidates[0];
    const cheque = chosenSource ? chosenSource.extracted_check_data : named;
    setFormInitial({
      ...EMPTY_VENDOR,
      vendor_code: '',
      source: 'manual',
      display_name: named.payee_name || cluster.vendor_name || '',
      address_line1: cheque.payee_address_line1 || '',
      address_line2: cheque.payee_address_line2 || '',
      city: cheque.payee_city || '',
      state: cheque.payee_state || '',
      postal_code: cheque.payee_postal_code || '',
    });
    setCreatingFor(cluster);
    fetchNextVendorCode({ branch_id: branchId }, (_m: string, resp: any) => {
      const next = resp?.data?.data?.vendor_code;
      if (next) setFormInitial((current) => ({ ...current, vendor_code: next }));
    });
  };

  const createThenAssign = (values: VendorFormValues) => {
    const cluster = creatingFor;
    if (!cluster) return;

    setCreating(true);
    const payload: Record<string, any> = {
      branch_id: branchId,
      display_name: values.display_name.trim(),
      status: Number(values.status),
      recipient_edelivery_consent: values.recipient_edelivery_consent,
      vendor_code: values.vendor_code.trim(),
    };
    ['contact_name', 'email', 'phone', 'address_line1', 'address_line2', 'city', 'state',
      'postal_code', 'default_form_type', 'default_box'].forEach((field) => {
      const value = String((values as any)[field] ?? '').trim();
      if (value) payload[field] = value;
    });
    if (values.vendor_type !== '') payload.vendor_type = Number(values.vendor_type);
    if (values.default_account_chart_id) payload.default_account_chart_id = values.default_account_chart_id;
    if (values.is_1099_eligible !== '') payload.is_1099_eligible = Number(values.is_1099_eligible);
    if (values.vendor_type === '2') {
      payload.is_1099_eligible = 0;
      payload.default_form_type = null;
      payload.default_box = null;
    }

    createVendor(payload, (_m: string, resp: any) => {
      setCreating(false);
      setCreatingFor(null);
      const created = resp?.data?.data;
      const newId = created?.id;
      if (!newId) {
        toast.error(t('errors.created_without_id'));
        load();
        return;
      }
      // Keep the new vendor selectable: if assignment fails the person retries
      // against it rather than creating a second one.
      setVendors((list) => [
        { value: newId, label: created.vendor_code ? `${created.display_name} (${created.vendor_code})` : created.display_name },
        ...list,
      ]);
      setChosen((c) => ({ ...c, [cluster.payee_key]: newId }));
      assign(cluster, newId);
    }, (message: string) => {
      setCreating(false);
      toast.error(message || t('errors.create_failed'));
    });
  };

  // Only payments whose payee is actually known belong here. The endpoint
  // returns every expense without a vendor, which on a real branch is mostly
  // Venmo, card purchases and transfers between the customer's own accounts --
  // none of which is a 1099 vendor payment. A cheque is what names a payee.
  const hasCheque = (c: PayeeCluster) => c.transactions.some((t) => t.extracted_check_data);
  const visible = clusters.filter(hasCheque);

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;
  }

  return (
    <div className="main-container flex-grow-1">
      <div className="container-fluid py-3">
        <h5 className="fw-bold mb-1">{t('title')}</h5>
        <small className="text-muted d-block mb-3">
          {t('subtitle')}
        </small>

        {resolvedAny && (
          <div className="alert alert-success d-flex flex-wrap justify-content-between align-items-center gap-2" role="status">
            <span>{visible.length === 0 ? t('next_step.all_resolved') : t('next_step.some_remaining')}</span>
            <Link to={URLS.DEPOSIT_GL_CLASSIFICATION_STATUS} className="btn btn-sm btn-success">
              {t('next_step.go_to_deposits')}
            </Link>
          </div>
        )}

        {visible.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <p className="mb-0">{t('empty.no_results')}</p>
          </div>
        ) : visible.map((cluster) => {
          const evidence = cluster.transactions.find((t) => t.extracted_check_data)?.extracted_check_data;
          const isOpen = expanded === cluster.payee_key;

          return (
            <div key={cluster.payee_key} className="card mb-3 border shadow-sm">
              <div className="card-body">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                  <div>
                    <span className="fw-semibold">{cluster.vendor_name}</span>
                    {evidence && <Badge bg="info" text="dark" className="ms-2">{t('badges.from_cheque')}</Badge>}
                    <div className="small text-muted">
                      {t('counts.transactions', { count: cluster.transaction_count })} · {money(cluster.total_amount)}
                      {cluster.earliest_date && ` · ${t('cluster.date_range', { from: cluster.earliest_date, to: cluster.latest_date })}`}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2" style={{ minWidth: 340 }}>
                    <div style={{ minWidth: 220 }}>
                      <Select
                        options={vendors}
                        isClearable
                        placeholder={t('actions.assign_placeholder')}
                        value={vendors.find((v) => v.value === chosen[cluster.payee_key]) || null}
                        onChange={(option: any) =>
                          setChosen((c) => ({ ...c, [cluster.payee_key]: option ? option.value : null }))}
                      />
                    </div>
                    <Button
                      size="sm"
                      disabled={saving === cluster.payee_key || !chosen[cluster.payee_key]}
                      onClick={() => confirm(cluster)}
                    >
                      {saving === cluster.payee_key ? t('actions.assigning') : t('actions.confirm')}
                    </Button>
                    {evidence && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        disabled={saving === cluster.payee_key}
                        onClick={() => startCreateFromCheque(cluster)}
                      >
                        {t('actions.new_vendor')}
                      </Button>
                    )}
                  </div>
                </div>

                {chequeAddresses(cluster).length > 1 && (
                  <div className="small mt-2" style={{ color: '#b45309' }}>
                    {t('cluster.multiple_addresses', { count: chequeAddresses(cluster).length })}
                    <div className="d-flex flex-wrap gap-2 mt-1">
                      {chequeAddresses(cluster).map((t) => (
                        <Button
                          key={t.transaction_id}
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => startCreateFromCheque(cluster, t.transaction_id)}
                        >
                          {(addressOf(t.extracted_check_data) || []).filter(Boolean).join(', ')}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {evidence && (
                  <div className="small text-muted mt-2">
                    {t('evidence.cheque_number', { number: evidence.check_number || '--' })}
                    {evidence.check_date && ` ${t('evidence.dated', { date: evidence.check_date })}`}
                    {evidence.memo && ` · ${t('evidence.memo', { memo: evidence.memo })}`}
                    {evidence.source_page && ` · ${t('evidence.page', { page: evidence.source_page })}`}
                    {evidence.source_file_id ? (
                      <Button variant="link" size="sm" className="p-0 ms-2 align-baseline"
                        onClick={() => openEvidence(evidence)}>
                        {t('actions.view_statement')}
                      </Button>
                    ) : (
                      <span className="ms-2">· {t('errors.source_unavailable')}</span>
                    )}
                  </div>
                )}

                <Button
                  variant="link"
                  size="sm"
                  className="p-0 mt-2"
                  onClick={() => setExpanded(isOpen ? null : cluster.payee_key)}
                >
                  {isOpen ? t('actions.hide_transactions') : t('actions.show_transactions', { count: cluster.transaction_count })}
                </Button>

                {isOpen && (
                  <div className="table-responsive mt-2">
                    <table className="table table-sm mb-0">
                      <thead className="table-light">
                        <tr><th>{t('table.headers.date')}</th><th>{t('table.headers.description')}</th><th className="text-end">{t('table.headers.amount')}</th></tr>
                      </thead>
                      <tbody>
                        {cluster.transactions.map((t) => (
                          <tr key={t.transaction_id}>
                            <td className="small">{t.posting_date || '--'}</td>
                            <td className="small">{t.description || '--'}</td>
                            <td className="small text-end">{money(t.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {creatingFor && (
        <VendorFormModal
          show
          saving={creating}
          initial={formInitial}
          onClose={() => setCreatingFor(null)}
          onSubmit={createThenAssign}
        />
      )}
    </div>
  );
};

export default Tax1099PayeeReview;
